import {
  Button,
  ButtonGroup,
  Popover,
  useDisclosure,
  RadioGroup,
  Stack,
  Text,
  NativeSelect,
  Icon,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { t } from "i18next";
import {
  AttributeDefinitions,
  AttributeName,
  CrashType,
  TreatmentType,
} from "@keita/lanepatrol-sdk";
import { ColorBy, ColorByType } from "../../../../map/models/ColorBy";
import { tScoreType } from "../../../../coding/utils/score-utils";
import { AttributeUtils } from "../../../../coding/utils/attribute-utils";
import { CustomPopover } from "../../../../shared/views/CustomPopover";
import { ChevronDownIcon } from "../../../../shared/views/icons";

const crashTypes = Object.values(CrashType);
const colorByTypes = Object.values(ColorByType);
const treatmentTypes = Object.values(TreatmentType);
const defaultScore = "global";
const defaultTreatment =
  TreatmentType.UpgradeExistingFacilityToAnOnRoadBicycleLaneWithLightSegregation;
const defaultAtribute = AttributeDefinitions.all.at(0)!;

interface Props {
  value: ColorBy;
  onChange: (colorBy: ColorBy) => void;
}
export const ColorBySelector: React.FC<Props> = ({ value, onChange }) => {
  const popover = useDisclosure();
  const [colorBy, setColorBy] = useState(value);

  useEffect(() => {
    if (popover.open) setColorBy(value);
    //eslint-disable-next-line
  }, [popover.open]);

  const changeType = (type: ColorByType) => {
    if (ColorByType.Score === type) {
      setColorBy({ type, scoreKey: defaultScore });
    } else if (ColorByType.Treatment === type) {
      setColorBy({ type, treatmentType: defaultTreatment });
    } else if (ColorByType.Attribute === type) {
      setColorBy({ type, attributeName: defaultAtribute.name });
    }
  };

  const submit = () => {
    onChange(colorBy);
    popover.onClose();
  };

  return (
    <CustomPopover
      open={popover.open}
      trigger={
        <Button
          colorPalette="primary"
          fontSize={"sm"}
          size="sm"
          variant="outline"
          onClick={popover.onToggle}
        >
          <Text lineClamp={1} whiteSpace="pre-line">
            {ColorByType.Score === value.type && tScoreType(value.scoreKey)}
            {ColorByType.Treatment === value.type &&
              t(`sdk:treatments.${value.treatmentType}`)}
            {ColorByType.Attribute === value.type &&
              AttributeUtils.getLabel(value.attributeName)}
          </Text>
          <Icon as={ChevronDownIcon} />
        </Button>
      }
      onClose={popover.onClose}
      width={400}
      maxH="360px"
    >
      {popover.open && (
        <>
          <Popover.Header>{t("map:header.lineColor")}</Popover.Header>
          <Popover.Body overflowY="auto">
            <NativeSelect.Root mb={2}>
              <NativeSelect.Field
                onChange={(event) =>
                  changeType(event.target.value as ColorByType)
                }
                value={colorBy.type}
              >
                {colorByTypes.map((type, i) => (
                  <option value={type} key={i}>
                    {t(`map:colorBy.type.${type}`)}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>

            {ColorByType.Score === colorBy.type && (
              <RadioGroup.Root
                py={2}
                value={colorBy.scoreKey}
                onValueChange={({ value }) => {
                  setColorBy({
                    type: ColorByType.Score,
                    scoreKey: value as CrashType,
                  });
                }}
                size="sm"
                colorPalette={"primary"}
              >
                <Stack>
                  <RadioGroup.Item key="global" value="global">
                    <RadioGroup.ItemHiddenInput />
                    <RadioGroup.ItemIndicator />
                    <RadioGroup.ItemText>
                      {tScoreType("global")}
                    </RadioGroup.ItemText>
                  </RadioGroup.Item>
                  {crashTypes.map((key, i) => (
                    <RadioGroup.Item key={i} value={key}>
                      <RadioGroup.ItemHiddenInput />
                      <RadioGroup.ItemIndicator />
                      <RadioGroup.ItemText>
                        {tScoreType(key)}
                      </RadioGroup.ItemText>
                    </RadioGroup.Item>
                  ))}
                </Stack>
              </RadioGroup.Root>
            )}

            {ColorByType.Treatment === colorBy.type && (
              <RadioGroup.Root
                py={2}
                size="sm"
                colorPalette={"primary"}
                value={colorBy.treatmentType}
                onValueChange={({ value }) => {
                  setColorBy({
                    type: ColorByType.Treatment,
                    treatmentType: value as TreatmentType,
                  });
                }}
              >
                <Stack>
                  {treatmentTypes.map((type, i) => (
                    <RadioGroup.Item key={i} value={type}>
                      <RadioGroup.ItemHiddenInput />
                      <RadioGroup.ItemIndicator />
                      <RadioGroup.ItemText>
                        {t(`sdk:treatments.${type}`)}
                      </RadioGroup.ItemText>
                    </RadioGroup.Item>
                  ))}
                </Stack>
              </RadioGroup.Root>
            )}

            {ColorByType.Attribute === colorBy.type && (
              <RadioGroup.Root
                py={2}
                value={colorBy.attributeName}
                size="sm"
                colorPalette={"primary"}
                onValueChange={({ value }) => {
                  setColorBy({
                    type: ColorByType.Attribute,
                    attributeName: value as AttributeName,
                  });
                }}
              >
                <Stack>
                  {AttributeDefinitions.all.map((attribute, i) => (
                    <RadioGroup.Item key={i} value={`${attribute.name}`}>
                      <RadioGroup.ItemHiddenInput />
                      <RadioGroup.ItemIndicator />
                      <RadioGroup.ItemText>
                        {AttributeUtils.getLabel(attribute.name)}
                      </RadioGroup.ItemText>
                    </RadioGroup.Item>
                  ))}
                </Stack>
              </RadioGroup.Root>
            )}
          </Popover.Body>
          <Popover.Footer display="flex" justifyContent="flex-end">
            <ButtonGroup colorPalette={"primary"} size={"sm"}>
              <Button onClick={submit}>{t("map:filters.submitButton")}</Button>
            </ButtonGroup>
          </Popover.Footer>
        </>
      )}
    </CustomPopover>
  );
};
